import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
    IconPlus, IconTrash, IconPencil, IconTrendingUp, IconTrendingDown,
    IconWallet, IconAlertTriangle, IconCalendarStats,
    IconArrowDownLeft, IconArrowUpRight,
    IconPrinter, IconFileTypePdf, IconX, IconArrowRight,
    IconReceipt2,
    IconFileInvoice, IconUsers, IconUser, IconScale, IconCircleCheck,
} from '@tabler/icons-react';
import incomeExpenseService from '../../../services/incomeExpenseService';
import { notifications } from '@/lib/notifications';
import { useIncomeExpenseStore } from '../../../store/incomeExpenseStore';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import PageHeader from '../../../components/common/PageHeader';
import PaginationBar from '../../../components/common/PaginationBar';
import DatePickerField from '../../../components/common/DatePickerField';
import SelectField from '../../../components/common/SelectField';
import OperatorPayCard from '../../../components/income/OperatorPayCard';
import SettlementModal from '../../../components/income/SettlementModal';
import InvoicePage from './InvoicePage';
import CustomersTab from './CustomersTab';

// ── Bangla digit helpers ──────────────────────────────────────────────────────
const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
function toBn(str) { return String(str).replace(/[0-9]/g, d => BN_DIGITS[d]); }
function fmtAmount(amount, isBn) {
    const raw = Number(amount).toLocaleString('en-BD', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return isBn ? toBn(raw) : raw;
}

// ── Month / date helpers ──────────────────────────────────────────────────────
const EN_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const BN_MONTHS = ['জানুয়ারি','ফেব্রুয়ারি','মার্চ','এপ্রিল','মে','জুন','জুলাই','আগস্ট','সেপ্টেম্বর','অক্টোবর','নভেম্বর','ডিসেম্বর'];
const EN_SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const BN_SHORT_MONTHS = ['জানু','ফেব্রু','মার্চ','এপ্রি','মে','জুন','জুলা','আগ','সেপ্টে','অক্টো','নভে','ডিসে'];

function fmtMonth(monthStr, isBn) {
    const parts = (monthStr || '').split('-');
    if (parts.length >= 2) {
        const monthIdx = parseInt(parts[1], 10) - 1;
        const months = isBn ? BN_MONTHS : EN_MONTHS;
        if (monthIdx >= 0 && monthIdx < 12) {
            const year = isBn ? toBn(parts[0]) : parts[0];
            return `${months[monthIdx]} ${year}`;
        }
    }
    return monthStr;
}
function fmtShortMonth(monthStr, isBn) {
    const parts = (monthStr || '').split('-');
    if (parts.length >= 2) {
        const monthIdx = parseInt(parts[1], 10) - 1;
        const months = isBn ? BN_SHORT_MONTHS : EN_SHORT_MONTHS;
        if (monthIdx >= 0 && monthIdx < 12) return months[monthIdx];
    }
    return monthStr;
}
// Full month name (no year) — used for the chart x-axis labels.
function fmtMonthName(monthStr, isBn) {
    const parts = (monthStr || '').split('-');
    if (parts.length >= 2) {
        const monthIdx = parseInt(parts[1], 10) - 1;
        const months = isBn ? BN_MONTHS : EN_MONTHS;
        if (monthIdx >= 0 && monthIdx < 12) return months[monthIdx];
    }
    return monthStr;
}
// Everything user-facing is stamped in Bangladesh time (UTC+6), no matter where
// the browser actually sits.
const BD_TZ = 'Asia/Dhaka';
function fmtDate(dateStr, isBn) {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(isBn ? 'bn-BD' : 'en-BD', { timeZone: BD_TZ });
}
function fmtTime(dateStr, isBn) {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleTimeString(isBn ? 'bn-BD' : 'en-BD', {
        timeZone: BD_TZ, hour: '2-digit', minute: '2-digit', hour12: true,
    });
}
// Entry text is interpolated into the generated print/PDF document — escape it.
function esc(str) {
    return String(str ?? '').replace(/[&<>"]/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
    ));
}
// Linked customer's display name for an entry (Bangla preferred); '' when none.
const custOf = (e) => e?.customer?.applicant_name_bn || e?.customer?.applicant_name_en || '';
// দেনা পাওনা পরিশোধ row (pays back a পাওনা or pays off a দেনা).
const isSettle = (e) => e?.settlementOf === 'pawna' || e?.settlementOf === 'dena';
// দেনা পাওনা type chip → the backend `kind` filter.
const KIND_OF_FILTER = { '': 'denapawna', income: 'pawna', expense: 'dena', settlement: 'settlement' };
// Rows per export request — the backend caps a list at 5000.
const EXPORT_LIMIT = 5000;

// Print a self-contained HTML report straight to the browser's print dialog via a
// throwaway hidden iframe (its own document → no app CSS/@page leaks into it).
function printReportHtml(html) {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.src = url;
    iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } URL.revokeObjectURL(url); }, 1000);
    };
    document.body.appendChild(iframe);
}

// The PDF is ONE tall image sliced into A4 pages, so a row sitting on a page edge would be cut
// in half. Walk the rows top-down and push any row that would cross an edge onto the next page
// (blank spacer + a repeated column header), keeping a `margin` band at each page's top and a
// `bottomMargin` band (room for a pinned page footer) at its bottom.
function avoidPageSplits(doc, pageH, margin, bottomMargin = margin) {
    doc.querySelectorAll('tbody tr, .pdf-keep').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height > pageH - margin - bottomMargin) return; // taller than a page — let it split
        const pageEnd = (Math.floor(r.top / pageH) + 1) * pageH;
        if (r.bottom <= pageEnd - bottomMargin) return;
        const push = pageEnd + margin - r.top;
        if (el.tagName === 'TR') {
            const spacer = doc.createElement('tr');
            spacer.innerHTML = `<td colspan="99" style="height:${push}px;padding:0;border:none;background:#fff"></td>`;
            el.parentNode.insertBefore(spacer, el);
            const head = el.closest('table')?.querySelector('thead tr');
            if (head) el.parentNode.insertBefore(head.cloneNode(true), el);
        } else {
            el.style.marginTop = `${(parseFloat(doc.defaultView.getComputedStyle(el).marginTop) || 0) + push}px`;
        }
    });
}

// Render a self-contained HTML report → multi-page A4 PDF and download it. WHY
// html-to-image (toSvg): the browser's OWN engine paints the node into an SVG
// <foreignObject>, so the PDF is pixel-identical to the on-screen document (no design
// drift, unlike html2canvas). jsPDF then tiles the tall image across A4 pages.
async function reportToPdf(html, filename) {
    const paper = { w: 794, h: 1123 }; // A4 @ ~96dpi
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = `position:fixed;left:-10000px;top:0;border:0;background:#fff;width:${paper.w}px;`;
    document.body.appendChild(iframe);
    try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        try { await doc.fonts?.ready; } catch { /* fonts optional */ }
        await new Promise((r) => setTimeout(r, 400)); // let layout/logo settle
        // A `.pdf-footer` is pinned to the foot of EVERY page instead of trailing the last row:
        // lift it out of the flow, keep the rows clear of its band, then stamp a copy per page.
        const footer = doc.querySelector('.pdf-footer');
        const footH = footer ? footer.getBoundingClientRect().height : 0;
        const FOOT_GAP = 20; // footer's distance from the page's bottom edge
        footer?.remove();
        avoidPageSplits(doc, paper.h, 28, footer ? FOOT_GAP + footH + 10 : 28);
        let h = Math.max(paper.h, doc.body.scrollHeight || 0);
        h = Math.ceil((h - 2) / paper.h) * paper.h; // round up to whole A4 pages
        if (footer) {
            const padL = parseFloat(doc.defaultView.getComputedStyle(doc.body).paddingLeft) || 0;
            doc.body.style.position = 'relative';
            doc.body.style.minHeight = `${h}px`;
            for (let p = 1; p <= h / paper.h; p++) {
                const f = footer.cloneNode(true);
                f.style.cssText += `;position:absolute;left:${padL}px;right:${padL}px;top:${p * paper.h - FOOT_GAP - footH}px;margin:0`;
                doc.body.appendChild(f);
            }
        }
        iframe.style.height = `${h}px`;
        // Rasterize the report at 3× device pixels. `toPng` renders the node through the
        // browser's own SVG-foreignObject engine (same pixel-fidelity as toSvg, no
        // html2canvas drift) but rasterizes at `pixelRatio` directly — so text/logo stay
        // CRISP instead of being upscaled from a 1× bitmap (the old blur source).
        const { toPng } = await import('html-to-image');
        const imgData = await toPng(doc.body, { backgroundColor: '#ffffff', cacheBust: true, width: paper.w, height: h, pixelRatio: 3 });
        const { jsPDF } = await import('jspdf');
        const pageW = paper.w;
        const imgH = h;
        const pageH = paper.h;
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [pageW, pageH], compress: true });
        let remaining = imgH, position = 0;
        pdf.addImage(imgData, 'PNG', 0, position, pageW, imgH);
        remaining -= pageH;
        while (remaining > 1) {
            position -= pageH;
            pdf.addPage([pageW, pageH], 'portrait');
            pdf.addImage(imgData, 'PNG', 0, position, pageW, imgH);
            remaining -= pageH;
        }
        pdf.save(`${filename || 'sohozkaj-hisab'}.pdf`);
    } finally {
        document.body.removeChild(iframe);
    }
}

// ═════════════════════════════════════════════════════════════════════════════
export default function IncomePage() {
    const { t, currentLanguage } = useTranslation();
    const { openModal } = useIncomeExpenseStore();
    const { user } = useAuthStore();
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);
    const officeSpaces = useOfficeSpaceStore((s) => s.officeSpaces);
    const isBn = currentLanguage === 'bn';
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = tabParam === 'invoice' ? 'invoice' : tabParam === 'customers' ? 'customers' : tabParam === 'denapawna' ? 'denapawna' : 'income';
    // দেনা পাওনা view: the SAME ledger module, but only baki entries — income-baki
    // shown as পাওনা (receivable) and expense-baki as দেনা (payable). The regular
    // আয় ব্যয় tab stays untouched (it includes নগদ + বাকি together).
    const isDena = activeTab === 'denapawna';
    const setActiveTab = useCallback((tab) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 'income') next.delete('tab');
            else next.set('tab', tab);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // A customer chosen from the Customers tab, handed to the invoice form to prefill.
    const [invoiceCustomer, setInvoiceCustomer] = useState(null);
    const startInvoiceForCustomer = useCallback((customer) => {
        setInvoiceCustomer(customer);
        setActiveTab('invoice');
    }, [setActiveTab]);

    // An invoice to edit, handed to the invoice form (from the customer-details page).
    const [editInvoice, setEditInvoice] = useState(null);
    // A full invoice draft (customer + service + amount) handed from the income modal's
    // "Save & Invoice" button.
    const [invoiceDraft, setInvoiceDraft] = useState(null);

    // Actions arrive via router state — from the customer-details page (a fresh mount)
    // OR from the income modal while already on this page (no remount). So we re-run on
    // every navigation (location.state changes identity) and dedupe by the state object
    // so the same payload is never consumed twice.
    const location = useLocation();
    const consumedStateRef = useRef(null);
    useEffect(() => {
        const st = location.state;
        if (!st || consumedStateRef.current === st) return;
        consumedStateRef.current = st;
        if (st.invoiceDraft) {
            setInvoiceDraft(st.invoiceDraft);
            setActiveTab('invoice');
        } else if (st.invoiceCustomer) {
            startInvoiceForCustomer(st.invoiceCustomer);
        } else if (st.editInvoice) {
            setEditInvoice(st.editInvoice);
            setActiveTab('invoice');
        } else {
            return;
        }
        // Clear so a refresh/back doesn't re-trigger (the dedupe ref already guards
        // in-memory re-runs; this drops the entry from the browser history stack).
        window.history.replaceState({}, '');
    }, [location.state, startInvoiceForCustomer, setActiveTab]);

    const TYPE_LABELS = { income: t('incomeExpense.income'), expense: t('incomeExpense.expense') };
    const CAT_LABELS  = {
        image_generation: t('incomeExpense.categoryImage'),
        doc_generation:   t('incomeExpense.categoryDoc'),
        manual:           t('incomeExpense.categoryManual'),
        ai_template:      t('incomeExpense.categoryAiTemplate'),
        print_media:      t('incomeExpense.categoryPrintMedia'),
        manual_edit:      t('incomeExpense.categoryManualEdit'),
    };

    const [summary,      setSummary]      = useState(null);
    const [entries,      setEntries]      = useState([]);
    const [page,         setPage]         = useState(1);
    const [totalPages,   setTotalPages]   = useState(1);
    const [total,        setTotal]        = useState(0);
    const [loading,      setLoading]      = useState(true);
    const [filterType,   setFilterType]   = useState('');
    const [dueFilter,    setDueFilter]    = useState(''); // '' all | 'true' due | 'false' cash
    const [dateFrom,     setDateFrom]     = useState('');
    const [dateTo,       setDateTo]       = useState('');
    const [activePreset, setActivePreset] = useState('all');
    // Shop / operator filters (owner only). `operatorKey` is "<officeSpaceId>:<operatorId>" so picking an
    // operator also pins the shop (the same person can work in more than one of the owner's shops).
    const [shopFilter,   setShopFilter]   = useState('');
    const [operatorKey,  setOperatorKey]  = useState('');
    const [deleting,     setDeleting]     = useState(null);
    const [confirmId,    setConfirmId]    = useState(null);
    const [exportBusy,   setExportBusy]   = useState(null);  // 'pdf' | 'print' while fetching/rendering
    // পরিশোধ modal (দেনা পাওনা): open + the settlement row being edited (null = new).
    const [settleModal,  setSettleModal]  = useState({ open: false, entry: null });

    const fmt = (d) => d.toISOString().slice(0, 10);
    // Local-safe YYYY-MM-DD helpers for the shadcn date picker (no UTC shift).
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const parseISO = (s) => (s ? new Date(`${s}T00:00:00`) : null);

    const PRESETS = [
        { key: 'today',     label: t('incomeExpense.presetToday'),     get() { const d = fmt(new Date()); return [d, d]; } },
        { key: 'yesterday', label: t('incomeExpense.presetYesterday'), get() { const d = new Date(); d.setDate(d.getDate()-1); const s = fmt(d); return [s, s]; } },
        { key: '7d',        label: t('incomeExpense.preset7d'),        get() { const d = new Date(); d.setDate(d.getDate()-6); return [fmt(d), fmt(new Date())]; } },
        { key: '15d',       label: t('incomeExpense.preset15d'),       get() { const d = new Date(); d.setDate(d.getDate()-14); return [fmt(d), fmt(new Date())]; } },
        { key: '30d',       label: t('incomeExpense.preset30d'),       get() { const d = new Date(); d.setDate(d.getDate()-29); return [fmt(d), fmt(new Date())]; } },
        { key: 'month',     label: t('incomeExpense.presetMonth'),     get() { const d = new Date(); d.setDate(1); return [fmt(d), fmt(new Date())]; } },
        { key: '6m',        label: t('incomeExpense.preset6m'),        get() { const d = new Date(); d.setMonth(d.getMonth()-6); return [fmt(d), fmt(new Date())]; } },
        { key: 'all',       label: t('incomeExpense.presetAll'),       get() { return ['', '']; } },
    ];

    const applyPreset = (preset) => {
        const [from, to] = preset.get();
        setActivePreset(preset.key);
        setDateFrom(from);
        setDateTo(to);
    };

    const ownedSpaces = useMemo(
        () => (user?.role === 'OPERATOR' ? [] : (officeSpaces || []).filter((sp) => sp.ownerId === user?.id)),
        [officeSpaces, user?.id, user?.role]
    );
    const shopLabel = (sp) => sp?.businessProfile?.shopName || sp?.name || '';
    const shopOptions = useMemo(() => [
        { value: '', label: t('operatorPay.allShops') },
        ...ownedSpaces.map((sp) => ({ value: String(sp.id), label: shopLabel(sp) })),
    ], [ownedSpaces, t]); // eslint-disable-line react-hooks/exhaustive-deps
    const operatorOptions = useMemo(() => [
        { value: '', label: t('operatorPay.allOperators') },
        ...ownedSpaces
            .filter((sp) => !shopFilter || String(sp.id) === shopFilter)
            .flatMap((sp) => (sp.operators || []).map((op) => ({
                value: `${sp.id}:${op.operatorId}`,
                label: ownedSpaces.length > 1 ? `${op.operator?.name} · ${shopLabel(sp)}` : (op.operator?.name || ''),
            }))),
    ], [ownedSpaces, shopFilter, t]); // eslint-disable-line react-hooks/exhaustive-deps
    const hasOperatorFilters = ownedSpaces.length > 1 || ownedSpaces.some((sp) => (sp.operators || []).length > 0);
    const [opSpaceId, opId] = operatorKey ? operatorKey.split(':') : [null, null];
    // Server-side scoping params for list + summary.
    const scopeParams = useMemo(() => ({
        officeSpaceId: opSpaceId || shopFilter || undefined,
        operatorId: opId || undefined,
    }), [opSpaceId, opId, shopFilter]);

    // পরিশোধ is a দেনা পাওনা-only type chip — drop it when switching back to আয় ব্যয়.
    useEffect(() => { if (!isDena && filterType === 'settlement') setFilterType(''); }, [isDena, filterType]);

    // List filters shared by the page list and the full PDF/print export. দেনা পাওনা asks
    // by `kind` (baki পাওনা / দেনা / পরিশোধ); আয় ব্যয় by type + নগদ/বাকি.
    const listParams = useMemo(() => ({
        ...(isDena
            ? { kind: KIND_OF_FILTER[filterType] || 'denapawna' }
            : { type: (filterType !== 'settlement' && filterType) || undefined, isDue: dueFilter || undefined }),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        ...scopeParams,
    }), [isDena, filterType, dueFilter, dateFrom, dateTo, scopeParams]);

    const load = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const [sumRes, listRes] = await Promise.all([
                incomeExpenseService.summary({ dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, ...scopeParams }),
                incomeExpenseService.list({ page: p, limit: 15, ...listParams }),
            ]);
            setSummary(sumRes.data);
            setEntries(listRes.data ?? []);
            setTotalPages(listRes.pagination?.totalPages ?? 1);
            setTotal(listRes.pagination?.total ?? listRes.total ?? 0);
            setPage(p);
        } catch (err) {
            notifications.show({ message: err.message || t('incomeExpense.loadError'), color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [listParams, dateFrom, dateTo, scopeParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(1); }, [load]);

    const { isOpen } = useIncomeExpenseStore();
    const wasOpen = useRef(false);
    useEffect(() => {
        if (wasOpen.current && !isOpen) load(1);
        wasOpen.current = isOpen;
    }, [isOpen, load]);

    const handleDelete = async () => {
        const id = confirmId;
        setConfirmId(null);
        setDeleting(id);
        try {
            await incomeExpenseService.remove(id);
            notifications.show({ message: t('incomeExpense.deleted'), color: 'green' });
            load(page);
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
        } finally {
            setDeleting(null);
        }
    };

    // Edit reuses the global add-entry modal in edit mode (prefill carries the id).
    // Closing the modal refreshes the list via the wasOpen effect below.
    const startEdit = (entry) => {
        // পরিশোধ rows have their own modal (customer → side → amount).
        if (isSettle(entry)) { setSettleModal({ open: true, entry }); return; }
        const raw = entry.date || entry.createdAt;
        const d = raw ? new Date(raw) : null;
        const dateISO = d && !isNaN(d)
            ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            : undefined;
        // Preserve the entry's own time so re-saving an edit doesn't shift it.
        const timeHM = d && !isNaN(d)
            ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
            : undefined;
        openModal({
            id: entry.id,
            name: entry.name || '',
            type: entry.type || 'income',
            amount: entry.amount,
            isDue: entry.isDue || false,
            note: entry.note || '',
            ...(dateISO ? { date: dateISO } : {}),
            ...(timeHM ? { time: timeHM } : {}),
            customerId: entry.customerId ?? null,
            customerName: custOf(entry),
            customerPhone: entry.customer?.mobile_number || '',
            // Editing from the দেনা পাওনা tab keeps that mode (blue header, দেনা/পাওনা
            // labels, no নগদ/বাকি toggle) instead of the plain আয়/ব্যয় modal.
            ...(isDena ? { denaPawna: true } : {}),
        });
    };

    // ── Chart helpers ─────────────────────────────────────────────────────────
    const chartMonths = useMemo(() => {
        if (!summary?.byMonth?.length) return [];
        const months = [...summary.byMonth].slice(-6);
        const maxVal = Math.max(...months.map(m => Math.max(m.income, m.expense, m.due || 0)), 1);
        return months.map(m => ({
            ...m,
            incomeH: (m.income / maxVal) * 100,
            expenseH: (m.expense / maxVal) * 100,
            dueH: ((m.due || 0) / maxVal) * 100,
            label: fmtMonthName(m.month, isBn),
        }));
    }, [summary, isBn]);

    // Only surface the due series when there's actually due data to show.
    const hasDueData = useMemo(() => chartMonths.some(m => (m.due || 0) > 0), [chartMonths]);

    // ── Print/PDF ─────────────────────────────────────────────────────────────
    // Exports cover EVERY row matching the current filters, not just the visible page.
    const fetchExportRows = async () => {
        const res = await incomeExpenseService.list({ page: 1, limit: EXPORT_LIMIT, ...listParams });
        return res.data ?? [];
    };

    const buildPrintHTML = (rows) => {
        const logoUrl = `${window.location.origin}/images/logo.svg`;
        // Report header shows the ACTIVE shop's business profile (name, address, phone,
        // email) — NOT the personal user account. Falls back to the user's own fields
        // only when the shop profile hasn't set them.
        const bp = selectedOfficeSpace?.businessProfile || {};
        const shopName    = bp.shopName || selectedOfficeSpace?.name || user?.shopName || '';
        const shopAddress = bp.businessAddress || user?.businessAddress || '';
        const shopPhone   = bp.shopPhone || user?.phone || '';
        const shopEmail   = bp.shopEmail || '';
        const now = new Date();
        const printDate = now.toLocaleDateString(isBn ? 'bn-BD' : 'en-BD', { timeZone: BD_TZ, year: 'numeric', month: 'long', day: 'numeric' });
        const printTime = now.toLocaleTimeString(isBn ? 'bn-BD' : 'en-BD', { timeZone: BD_TZ, hour: '2-digit', minute: '2-digit', hour12: true });

        let periodLabel = t('incomeExpense.presetAll');
        if (dateFrom && dateTo && dateFrom === dateTo) {
            periodLabel = fmtDate(dateFrom, isBn);
        } else if (dateFrom && dateTo) {
            periodLabel = `${fmtDate(dateFrom, isBn)} — ${fmtDate(dateTo, isBn)}`;
        } else if (dateFrom) {
            periodLabel = `${fmtDate(dateFrom, isBn)} →`;
        }
        // দেনা পাওনা export/print: report title + type labels read পাওনা/দেনা/পরিশোধ.
        const reportTitle = isDena ? t('incomeExpense.denaPawnaReportTitle') : t('incomeExpense.tabTitle');
        const typeLabelOf = (ty) => ty === 'settlement' ? t('incomeExpense.settlement') : isDena ? t(ty === 'income' ? 'incomeExpense.pawna' : 'incomeExpense.dena') : TYPE_LABELS[ty];
        const filterTypeLabel = filterType ? typeLabelOf(filterType) : t('incomeExpense.typeAll');
        // Shop contact details on ONE line (compact দেনা পাওনা header).
        const shopLine = [shopAddress, shopPhone, shopEmail].filter(Boolean).map(esc).join(' &middot; ');

        // ── দেনা পাওনা: compact balance-sheet list (no ধরন / খাত columns, date without time).
        // All types → separate দেনা / পাওনা / পরিশোধ amount columns, each totalled; one type
        // filtered → a single পরিমাণ column with its total.
        if (isDena) {
            const sideOf = (e) => (isSettle(e) ? 'settlement' : e.type === 'income' ? 'pawna' : 'dena');
            const totals = { dena: 0, pawna: 0, settlement: 0 };
            rows.forEach((e) => { totals[sideOf(e)] += Number(e.amount) || 0; });
            const COLOR = { dena: '#dc2626', pawna: '#059669', settlement: '#4f46e5' };
            const money = (n) => `৳${fmtAmount(n, isBn)}`;
            const allTypes = !filterType;
            const cols = allTypes ? ['dena', 'pawna', 'settlement'] : null;
            const colLabel = { dena: t('incomeExpense.dena'), pawna: t('incomeExpense.pawna'), settlement: t('incomeExpense.settlement') };
            const nameCell = (e) => {
                const cust = custOf(e);
                return `<span class="n">${esc(e.name || '')}</span>`
                    + (cust && cust !== e.name ? `<span class="c"> · ${esc(cust)}</span>` : '')
                    + (isSettle(e) ? `<span class="tag">${esc(t(e.settlementOf === 'pawna' ? 'incomeExpense.settlePawna' : 'incomeExpense.settleDena'))}</span>` : '')
                    + (e.note ? `<span class="nt"> — ${esc(e.note)}</span>` : '');
            };
            const body = rows.map((e, i) => {
                const side = sideOf(e);
                const amtCells = allTypes
                    ? cols.map((c) => `<td class="am"${c === side ? ` style="color:${COLOR[c]}"` : ''}>${c === side ? money(e.amount) : ''}</td>`).join('')
                    : `<td class="am" style="color:${COLOR[side]}">${money(e.amount)}</td>`;
                return `<tr class="${i % 2 === 1 ? 'alt' : ''}"><td class="sl">${isBn ? toBn(i + 1) : i + 1}</td><td>${nameCell(e)}</td><td class="dt">${fmtDate(e.createdAt, isBn)}</td>${amtCells}</tr>`;
            }).join('');
            const onlySide = allTypes ? null : KIND_OF_FILTER[filterType]; // pawna | dena | settlement
            const totalRow = allTypes
                ? `<tr class="tot"><td colspan="3">${t('incomeExpense.total')}</td>${cols.map((c) => `<td class="am" style="color:${COLOR[c]}">${money(totals[c])}</td>`).join('')}</tr>`
                : `<tr class="tot"><td colspan="3">${t('incomeExpense.total')} (${esc(colLabel[onlySide])})</td><td class="am" style="color:${COLOR[onlySide]}">${money(totals[onlySide])}</td></tr>`;
            const net = totals.pawna - totals.dena;
            // নিট sits INSIDE the table (last body row) so it stays with the totals — after the
            // table it would land below print's repeating footer spacer, or alone on a new page.
            // The side the balance falls on reads as a word instead of a sign: দেনা ৳… / পাওনা ৳… (nothing at zero).
            const netSide = net < 0 ? `${t('incomeExpense.dena')} ` : net > 0 ? `${t('incomeExpense.pawna')} ` : '';
            const netRow = allTypes
                ? `<tr class="netrow"><td colspan="6" style="color:${net >= 0 ? '#1d4ed8' : '#f97316'}">${t('incomeExpense.denaPawnaNet')}: ${netSide}${money(Math.abs(net))}</td></tr>`
                : '';
            const countLabel = `${isBn ? toBn(rows.length) : rows.length} ${t('incomeExpense.entriesCount')}`;

            return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>SohozKaj — ${reportTitle}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Noto Sans Bengali',Arial,sans-serif;font-size:10.5px;color:#18181b;background:#fff;padding:20px 30px}
    .hdr{display:flex;align-items:center;justify-content:space-between;gap:16px;padding-bottom:4px}
    .hdr img{height:22px;display:block;flex:none}
    .shop{text-align:right;line-height:1.3;min-width:0}
    .shop-name{font-size:11px;font-weight:700}
    .shop-meta{font-size:9px;color:#52525b}
    .tbar{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:3px 0 5px;padding:2px 8px;background:#fff8ee;border-left:3px solid #e68a00}
    .tbar-title{font-size:11.5px;font-weight:700;color:#92400e}
    .tbar-meta{font-size:9px;color:#78716c;display:flex;gap:10px}
    table{width:100%;border-collapse:collapse}
    th{background:#f4f4f5;text-align:left;padding:4px 6px;font-size:9.5px;font-weight:600;color:#52525b;border:1px solid #e4e4e7;white-space:nowrap}
    td{padding:3px 6px;border:1px solid #ececef;vertical-align:top;line-height:1.35}
    tr.alt td{background:#fafafa}
    th.am,td.am{text-align:right;width:82px;white-space:nowrap;font-weight:600}
    .sl{width:28px;text-align:center;color:#a1a1aa}
    th.sl{color:#52525b}
    .dt{width:74px;white-space:nowrap}
    .n{font-weight:600}
    .c{color:#0369a1}
    .nt{color:#71717a}
    .tag{margin-left:5px;padding:0 4px;border:1px solid #c7d2fe;border-radius:3px;font-size:8.5px;color:#4f46e5;white-space:nowrap}
    tr.tot td{background:#f4f4f5;font-weight:700;border-top:2px solid #d4d4d8}
    tr.netrow td{border:none;background:#fff;text-align:right;font-size:11px;font-weight:700;padding-top:6px}
    .ftr{margin-top:12px;padding-top:4px;border-top:1px solid #e4e4e7;display:flex;justify-content:space-between;font-size:9px;line-height:1.4;color:#a1a1aa;background:#fff}
    .ftr strong{color:#e68a00}
    tfoot{display:none}
    /* Print: the footer is fixed to the bottom of every page; the repeating empty tfoot row
       reserves its height so table rows never run underneath it. */
    @media print{@page{margin:10mm;size:A4}body{padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}thead{display:table-header-group}tfoot{display:table-footer-group}tfoot td{height:24px;padding:0;border:none;background:#fff}tr{break-inside:avoid}tr.netrow{break-before:avoid}.ftr{position:fixed;left:0;right:0;bottom:0;margin:0}}
  </style>
</head>
<body>
  <div class="hdr">
    <img src="${logoUrl}" alt="SohozKaj" onerror="this.style.display='none'">
    <div class="shop">
      <div class="shop-name">${esc(shopName)}</div>
      ${shopLine ? `<div class="shop-meta">${shopLine}</div>` : ''}
    </div>
  </div>
  <div class="tbar">
    <div class="tbar-title">${reportTitle}</div>
    <div class="tbar-meta"><span>${periodLabel}</span><span>${filterTypeLabel}</span><span>${countLabel}</span></div>
  </div>
  <table>
    <thead><tr>
      <th class="sl">${t('incomeExpense.colSerial')}</th>
      <th>${t('incomeExpense.name')}</th>
      <th class="dt">${t('incomeExpense.colDate')}</th>
      ${allTypes ? cols.map((c) => `<th class="am">${colLabel[c]}</th>`).join('') : `<th class="am">${t('incomeExpense.amount')}</th>`}
    </tr></thead>
    <tbody>${body}${totalRow}${netRow}</tbody>
    <tfoot><tr><td colspan="99"></td></tr></tfoot>
  </table>
  <div class="ftr pdf-footer">
    <div>Generated by <strong>SohozKaj</strong> &mdash; sohozkaj.com</div>
    <div>${printDate} &nbsp;|&nbsp; ${printTime}</div>
  </div>
</body>
</html>`;
        }

        const rowsHtml = rows.map((e, i) => `
            <tr class="${i % 2 === 1 ? 'alt' : ''}">
                <td>
                    <div class="r-name">${esc(e.name || '')}</div>
                    ${custOf(e) ? `<div class="r-cust">${esc(t('incomeExpense.customersTab') || 'গ্রাহক')}: ${esc(custOf(e))}</div>` : ''}
                    ${e.note ? `<div class="r-note">${esc(e.note)}</div>` : ''}
                </td>
                <td>${CAT_LABELS[e.category] || e.category || ''}</td>
                <td class="r-date">${fmtDate(e.createdAt, isBn)}<span class="r-time">${fmtTime(e.createdAt, isBn)}</span></td>
                <td><span class="badge ${e.type}">${typeLabelOf(e.type)}</span></td>
                <td class="amt" style="color:${e.type === 'income' ? '#059669' : '#ef4444'}">
                    ${e.type === 'income' ? '+' : '-'}৳${fmtAmount(e.amount, isBn)}
                </td>
            </tr>
        `).join('');

        const summaryRows = !summary ? '' : (`
            <tr class="sum-row"><td colspan="4" style="color:#059669;font-weight:600">${t('incomeExpense.totalIncome')}</td><td class="amt" style="color:#059669">+৳${fmtAmount(summary.totalIncome, isBn)}</td></tr>
            <tr class="sum-row"><td colspan="4" style="color:#ef4444;font-weight:600">${t('incomeExpense.totalExpense')}</td><td class="amt" style="color:#ef4444">-৳${fmtAmount(summary.totalExpense, isBn)}</td></tr>
            <tr class="sum-row"><td colspan="4" style="color:#d97706;font-weight:600">${t('incomeExpense.dueGiven')}</td><td class="amt" style="color:#d97706">৳${fmtAmount(summary.totalDueIncome || 0, isBn)}</td></tr>
            <tr class="sum-row"><td colspan="4" style="color:#ea580c;font-weight:600">${t('incomeExpense.dueTaken')}</td><td class="amt" style="color:#ea580c">৳${fmtAmount(summary.totalDueExpense || 0, isBn)}</td></tr>
            <tr class="sum-row bal-row"><td colspan="4" style="color:${summary.balance >= 0 ? '#1d4ed8' : '#f97316'};font-weight:700">${t('incomeExpense.balance')}</td><td class="amt" style="color:${summary.balance >= 0 ? '#1d4ed8' : '#f97316'};font-size:15px">৳${fmtAmount(summary.balance, isBn)}</td></tr>
        `);

        return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>SohozKaj — ${reportTitle}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Noto Sans Bengali',Arial,sans-serif;font-size:13px;color:#18181b;background:#fff}
    .hdr{display:flex;align-items:flex-start;justify-content:space-between;padding:24px 32px 18px;border-bottom:3px solid #e68a00}
    .logo-wrap{display:flex;align-items:center;gap:10px}
    .logo-wrap img{height:38px;display:block}
    .brand{font-size:21px;font-weight:800;color:#e68a00;letter-spacing:-0.5px;line-height:1}
    .user-info{text-align:right}
    .user-name{font-size:13px;font-weight:700;color:#18181b}
    .user-meta{font-size:11px;color:#000;margin-top:4px;line-height:1.7}
    .tbar{background:#fff8ee;border-bottom:1px solid #fed7aa;padding:10px 32px;display:flex;align-items:center;justify-content:space-between}
    .tbar-title{font-size:14px;font-weight:700;color:#92400e}
    .tbar-meta{font-size:11px;color:#78716c;display:flex;gap:18px;align-items:center}
    .twrap{padding:20px 32px 0}
    table{width:100%;border-collapse:collapse}
    th{background:#f4f4f5;text-align:left;padding:8px 10px;font-size:11px;font-weight:600;color:#52525b;border-bottom:2px solid #e4e4e7;white-space:nowrap}
    td{padding:8px 10px;border-bottom:1px solid #f4f4f5;vertical-align:middle}
    tr.alt td{background:#fafafa}
    .amt{text-align:right;font-weight:600}
    th:nth-child(5){text-align:right}

    .r-name{font-weight:600}
    .r-cust{margin-top:2px;font-size:10.5px;line-height:1.4;color:#0369a1;font-weight:600}
    .r-note{margin-top:2px;font-size:10.5px;line-height:1.45;color:#71717a;max-width:230px;white-space:normal;word-break:break-word}
    .r-date{white-space:nowrap}
    .r-time{margin-left:6px;font-size:10.5px;color:#a1a1aa}
    .badge{display:inline-block;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:600}
    .badge.income{background:#f0fdf4;color:#059669}
    .badge.expense{background:#fef2f2;color:#ef4444}
    tr.sum-row td{border-top:2px solid #e4e4e7;border-bottom:none;padding-top:10px;padding-bottom:10px}
    tr.bal-row td{font-size:14px;font-weight:700}
    .ftr{margin:24px 32px 0;padding:12px 0;border-top:1px solid #e4e4e7;display:flex;align-items:center;justify-content:space-between}
    .ftr-brand{font-size:11px;color:#a1a1aa}
    .ftr-brand strong{color:#e68a00}
    .ftr-ts{font-size:11px;color:#a1a1aa}
    @media print{@page{margin:0.7cm;size:A4}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style>
</head>
<body>
  <div class="hdr">
    <div class="logo-wrap">
      <img src="${logoUrl}" alt="SohozKaj" style="height:42px;display:block" onerror="this.style.display='none'">
    </div>
    <div class="user-info">
      <div class="user-name">${esc(shopName)}</div>
      <div class="user-meta">
        ${shopAddress ? `<span>${esc(shopAddress)}</span><br>` : ''}
        ${shopPhone ? `<span>${esc(shopPhone)}</span>${shopEmail ? '<br>' : ''}` : ''}
        ${shopEmail ? `<span>${esc(shopEmail)}</span>` : ''}
      </div>
    </div>
  </div>
  <div class="tbar">
    <div class="tbar-title">${reportTitle}</div>
    <div class="tbar-meta"><span>${periodLabel}</span><span>${filterTypeLabel}</span></div>
  </div>
  <div class="twrap">
    <table>
      <thead><tr>
        <th>${t('incomeExpense.name')}</th>
        <th>${t('incomeExpense.colCategory')}</th>
        <th>${t('incomeExpense.colDate')}</th>
        <th>${t('incomeExpense.filterType')}</th>
        <th>${t('incomeExpense.amount')}</th>
      </tr></thead>
      <tbody>${rowsHtml}${summaryRows}</tbody>
    </table>
  </div>
  <div class="ftr pdf-keep">
    <div class="ftr-brand">Generated by <strong>SohozKaj</strong> &mdash; sohozkaj.com</div>
    <div class="ftr-ts">${printDate} &nbsp;|&nbsp; ${printTime}</div>
  </div>
</body>
</html>`;
    };

    // Print goes straight to the browser print dialog (no in-app preview); PDF builds a
    // real A4 PDF from the same HTML and downloads it. Both fetch all filtered rows first.
    const runExport = async (kind) => {
        if (exportBusy) return;
        setExportBusy(kind);
        try {
            const html = buildPrintHTML(await fetchExportRows());
            if (kind === 'print') printReportHtml(html);
            else await reportToPdf(html, `sohozkaj-${isDena ? 'dena-pawna' : 'hisab'}-${fmt(new Date())}`);
        } catch (err) {
            notifications.show({ message: err.message || t('common.error'), color: 'red' });
        } finally {
            setExportBusy(null);
        }
    };
    const handlePrint = () => runExport('print');
    const handleExportPdf = () => runExport('pdf');

    // Row type badge + amount styling. In দেনা পাওনা a পরিশোধ row reads indigo "পাওনা/দেনা পরিশোধ";
    // in আয় ব্যয় it keeps its cash type (আয়/ব্যয়) and gets a small পরিশোধ chip by the name.
    const settleInDena = (e) => isDena && isSettle(e);
    const rowBadgeCls = (e) => (settleInDena(e)
        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
        : e.type === 'income'
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300');
    const rowBadgeIcon = (e, size) => (settleInDena(e)
        ? <IconCircleCheck size={size} />
        : e.type === 'income' ? <IconTrendingUp size={size} /> : <IconTrendingDown size={size} />);
    const rowBadgeLabel = (e) => (settleInDena(e)
        ? t(e.settlementOf === 'pawna' ? 'incomeExpense.settlePawna' : 'incomeExpense.settleDena')
        : isDena ? t(e.type === 'income' ? 'incomeExpense.pawna' : 'incomeExpense.dena') : TYPE_LABELS[e.type]);
    const rowAmountCls = (e) => (settleInDena(e)
        ? 'text-indigo-600 dark:text-indigo-400'
        : e.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400');
    const rowAmountSign = (e) => (settleInDena(e) ? '' : e.type === 'income' ? '+' : '−');
    const settleChip = (e) => (!isDena && isSettle(e) ? (
        <span className="flex-shrink-0 text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded">
            {t('incomeExpense.settlement')}
        </span>
    ) : null);

    // Today's date for subtitle
    const todayStr = useMemo(() => {
        const now = new Date();
        return now.toLocaleDateString(isBn ? 'bn-BD' : 'en-BD', { year: 'numeric', month: 'long', day: 'numeric' });
    }, [isBn]);

    return (
        <div className="font-hind py-3 sm:py-6 space-y-5">

            <div className="mb-5">
                <PageHeader
                    title={t('incomeExpense.tabTitle')}
                    subtitle={t('incomeExpense.pageSubtitle') || t('incomeExpense.tabSubtitle')}
                    action={(activeTab === 'income' || isDena) && (
                        <div className="sm:hidden flex flex-shrink-0 items-center gap-1.5">
                            {isDena && (
                                <button
                                    type="button"
                                    onClick={() => setSettleModal({ open: true, entry: null })}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold rounded-lg transition-colors whitespace-nowrap"
                                >
                                    <IconCircleCheck size={15} />
                                    {t('incomeExpense.settlement')}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => openModal(isDena ? { denaPawna: true } : null)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors shadow-sm shadow-primary/20 whitespace-nowrap"
                            >
                                <IconPlus size={15} />
                                {t('incomeExpense.addNew')}
                            </button>
                        </div>
                    )}
                />
            </div>

            {/* ── Tabs + Add button (same row) ────────────────────────── */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [ms-overflow-style:none]" style={{ flexWrap: 'nowrap' }}>
                    {[
                        { value: 'income',    icon: <IconReceipt2 size={16} />,    label: t('incomeExpense.tabTitle') },
                        { value: 'denapawna', icon: <IconScale size={16} />,       label: t('incomeExpense.denaPawnaTab') },
                        { value: 'invoice',   icon: <IconFileInvoice size={16} />, label: t('incomeExpense.invoiceTab') || 'ইনভয়েস' },
                        { value: 'customers', icon: <IconUsers size={16} />,       label: t('incomeExpense.customersTab') || 'গ্রাহক' },
                    ].map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            onClick={() => setActiveTab(tab.value)}
                            className={`flex items-center gap-2 px-[15px] py-[9px] rounded-[11px] text-[13.5px] whitespace-nowrap flex-shrink-0 transition-colors border ${
                                activeTab === tab.value
                                    ? 'font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900'
                                    : 'font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>
                {(activeTab === 'income' || isDena) && (
                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                        {isDena && (
                            <button
                                type="button"
                                onClick={() => setSettleModal({ open: true, entry: null })}
                                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm shadow-indigo-600/20"
                            >
                                <IconCircleCheck size={15} />
                                {t('incomeExpense.settlement')}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => openModal(isDena ? { denaPawna: true } : null)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm shadow-primary/20"
                        >
                            <IconPlus size={15} />
                            {t('incomeExpense.addNew')}
                        </button>
                    </div>
                )}
            </div>

            {activeTab === 'invoice' ? (
                <InvoicePage
                    presetCustomer={invoiceCustomer}
                    onPresetConsumed={() => setInvoiceCustomer(null)}
                    presetEditInvoice={editInvoice}
                    onEditPresetConsumed={() => setEditInvoice(null)}
                    presetDraft={invoiceDraft}
                    onDraftConsumed={() => setInvoiceDraft(null)}
                    onLedgerChanged={() => load(1)}
                />
            ) : activeTab === 'customers' ? (
                <CustomersTab onCreateInvoice={startInvoiceForCustomer} />
            ) : (
            <>

            {/* ── Summary cards (দেনা পাওনা view: baki-only totals) ─────────── */}
            {summary && isDena && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                    {/* পাওনা — income entries on baki (customers owe you) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-amber-50/80 dark:bg-amber-900/15 rounded-2xl border border-amber-200 dark:border-amber-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">{t('incomeExpense.pawna')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-amber-700 dark:text-amber-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalDueIncome || 0, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconArrowDownLeft size={22} className="text-amber-600 dark:text-amber-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.pawna')}: ৳{fmtAmount(summary.totalDueIncome || 0, isBn)} ({t('incomeExpense.receivable')})</TooltipContent>
                    </Tooltip>
                    {/* দেনা — expense entries on baki (you owe others) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-orange-50/80 dark:bg-orange-900/15 rounded-2xl border border-orange-200 dark:border-orange-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1.5">{t('incomeExpense.dena')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-orange-700 dark:text-orange-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalDueExpense || 0, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconArrowUpRight size={22} className="text-orange-600 dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.dena')}: ৳{fmtAmount(summary.totalDueExpense || 0, isBn)} ({t('incomeExpense.payable')})</TooltipContent>
                    </Tooltip>
                    {/* পরিশোধ — পাওনা paid back + দেনা paid off (the two cards above stay as recorded) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-indigo-50/80 dark:bg-indigo-900/15 rounded-2xl border border-indigo-200 dark:border-indigo-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-1.5">{t('incomeExpense.settlement')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-indigo-700 dark:text-indigo-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalSettled || 0, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-indigo-100 dark:bg-indigo-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconCircleCheck size={22} className="text-indigo-600 dark:text-indigo-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            {t('incomeExpense.settlePawna')}: ৳{fmtAmount(summary.totalSettledPawna || 0, isBn)} · {t('incomeExpense.settleDena')}: ৳{fmtAmount(summary.totalSettledDena || 0, isBn)}
                        </TooltipContent>
                    </Tooltip>
                    {/* নিট — পাওনা − দেনা */}
                    {(() => { const net = (summary.totalDueIncome || 0) - (summary.totalDueExpense || 0);
                    // The side the balance falls on reads as a word, not a sign: দেনা ৳… / পাওনা ৳… (nothing at zero).
                    const netSide = net < 0 ? t('incomeExpense.dena') : net > 0 ? t('incomeExpense.pawna') : ''; return (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className={`rounded-2xl border p-3 sm:p-5 flex items-center justify-between cursor-default ${
                                net >= 0
                                    ? 'bg-sky-50/80 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/50'
                                    : 'bg-orange-50/80 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800/50'
                            }`}>
                                <div>
                                    <p className={`text-xs font-semibold mb-1.5 ${net >= 0 ? 'text-sky-600 dark:text-sky-400' : 'text-orange-600 dark:text-orange-400'}`}>{t('incomeExpense.denaPawnaNet')}</p>
                                    <p className={`text-lg sm:text-3xl font-bold tracking-tight ${net >= 0 ? 'text-sky-700 dark:text-sky-300' : 'text-orange-700 dark:text-orange-300'}`} style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        {netSide && <span className="mr-1.5 text-sm sm:text-lg font-semibold">{netSide}</span>}৳ {fmtAmount(Math.abs(net), isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconWallet size={22} className="text-primary dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.denaPawnaNet')}: {netSide ? `${netSide} ` : ''}৳{fmtAmount(Math.abs(net), isBn)}</TooltipContent>
                    </Tooltip>
                    ); })()}
                </div>
            )}

            {/* ── Summary cards ─────────────────────────────────────────────── */}
            {summary && !isDena && (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-4">
                    {/* Total Income */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-green-50/80 dark:bg-green-900/15 rounded-2xl border border-green-200 dark:border-green-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1.5">{t('incomeExpense.totalIncome')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-green-700 dark:text-green-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalIncome, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconTrendingUp size={22} className="text-primary dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.totalIncome')}: ৳{fmtAmount(summary.totalIncome, isBn)}</TooltipContent>
                    </Tooltip>

                    {/* Total Expense */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-rose-50/80 dark:bg-rose-900/15 rounded-2xl border border-rose-200 dark:border-rose-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-rose-500 dark:text-rose-400 mb-1.5">{t('incomeExpense.totalExpense')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-rose-600 dark:text-rose-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalExpense, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconTrendingDown size={22} className="text-primary dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.totalExpense')}: ৳{fmtAmount(summary.totalExpense, isBn)}</TooltipContent>
                    </Tooltip>

                    {/* Income Due — বাকি দিলাম (receivable) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-amber-50/80 dark:bg-amber-900/15 rounded-2xl border border-amber-200 dark:border-amber-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1.5">{t('incomeExpense.dueGiven')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-amber-700 dark:text-amber-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalDueIncome || 0, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconArrowDownLeft size={22} className="text-amber-600 dark:text-amber-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.dueGiven')}: ৳{fmtAmount(summary.totalDueIncome || 0, isBn)} ({t('incomeExpense.receivable')})</TooltipContent>
                    </Tooltip>

                    {/* Expense Due — বাকি নিলাম (payable) */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="bg-orange-50/80 dark:bg-orange-900/15 rounded-2xl border border-orange-200 dark:border-orange-800/50 p-3 sm:p-5 flex items-center justify-between cursor-default">
                                <div>
                                    <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1.5">{t('incomeExpense.dueTaken')}</p>
                                    <p className="text-lg sm:text-3xl font-bold text-orange-700 dark:text-orange-300 tracking-tight" style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        ৳ {fmtAmount(summary.totalDueExpense || 0, isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconArrowUpRight size={22} className="text-orange-600 dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.dueTaken')}: ৳{fmtAmount(summary.totalDueExpense || 0, isBn)} ({t('incomeExpense.payable')})</TooltipContent>
                    </Tooltip>

                    {/* Balance — kept LAST so নগদ/বাকি totals read before the net figure.
                        Spans full width on the 2-col mobile grid (it's the lone 5th card). */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className={`col-span-2 lg:col-span-1 rounded-2xl border p-3 sm:p-5 flex items-center justify-between cursor-default ${
                                summary.balance >= 0
                                    ? 'bg-sky-50/80 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800/50'
                                    : 'bg-orange-50/80 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800/50'
                            }`}>
                                <div>
                                    <p className={`text-xs font-semibold mb-1.5 ${
                                        summary.balance >= 0 ? 'text-sky-600 dark:text-sky-400' : 'text-orange-600 dark:text-orange-400'
                                    }`}>{t('incomeExpense.balance')}</p>
                                    <p className={`text-lg sm:text-3xl font-bold tracking-tight ${
                                        summary.balance >= 0 ? 'text-sky-700 dark:text-sky-300' : 'text-orange-700 dark:text-orange-300'
                                    }`} style={{ WebkitTextStroke: '0.6px currentColor' }}>
                                        {summary.balance < 0 ? '-' : ''}৳ {fmtAmount(Math.abs(summary.balance), isBn)}
                                    </p>
                                </div>
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-orange-100 dark:bg-orange-800/40 flex items-center justify-center flex-shrink-0">
                                    <IconWallet size={22} className="text-primary dark:text-orange-400" />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{t('incomeExpense.balance')}: {summary.balance < 0 ? '-' : ''}৳{fmtAmount(Math.abs(summary.balance), isBn)}</TooltipContent>
                    </Tooltip>
                </div>
            )}

            {/* ── Two-column: Chart + Monthly Summary (mixes নগদ data → hidden in দেনা পাওনা) ── */}
            {summary && !isDena && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Income vs Expense Bar Chart */}
                    {chartMonths.length > 0 && (
                        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 p-5 flex flex-col">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{t('incomeExpense.incomeVsExpense')}</h3>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                                        <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('incomeExpense.income')}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                        <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('incomeExpense.expense')}</span>
                                    </div>
                                    {hasDueData && (
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                            <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('incomeExpense.due')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* CSS Bar chart — evenly distributed so a few months don't leave
                                a big blank gap on the right (was justify-start + flex-1). */}
                            {/* mt-auto: the card is stretched to the monthly-summary's height, so push the
                                bars (and their month labels) to the BOTTOM instead of leaving a gap under them. */}
                            <div className="mt-auto flex items-end justify-evenly gap-2 h-40">
                                {chartMonths.map((m) => (
                                    <div key={m.month} className="flex flex-col items-center gap-1">
                                        <div className="flex items-end gap-1 w-full justify-center h-32">
                                            {/* Income bar */}
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className="w-4 sm:w-5 rounded-t-md bg-emerald-500/80 transition-all duration-500 cursor-pointer"
                                                        style={{ height: `${Math.max(m.incomeH, 3)}%` }}
                                                    />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{t('incomeExpense.income')}: ৳{fmtAmount(m.income, isBn)}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                            {/* Expense bar */}
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className="w-4 sm:w-5 rounded-t-md bg-red-400/80 transition-all duration-500 cursor-pointer"
                                                        style={{ height: `${Math.max(m.expenseH, 3)}%` }}
                                                    />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{t('incomeExpense.expense')}: ৳{fmtAmount(m.expense, isBn)}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                            {/* Due bar */}
                                            {hasDueData && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div
                                                            className="w-4 sm:w-5 rounded-t-md bg-amber-400/80 transition-all duration-500 cursor-pointer"
                                                            style={{ height: `${Math.max(m.dueH, 3)}%` }}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>{t('incomeExpense.due')}: ৳{fmtAmount(m.due || 0, isBn)}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium mt-1">{m.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Monthly Summary */}
                    {summary?.byMonth?.length > 0 && (
                        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800/60">
                                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{t('incomeExpense.monthlySummary')}</h3>
                                <span className="text-xs text-zinc-400 dark:text-zinc-500">
                                    {isBn ? toBn(summary.byMonth.length) : summary.byMonth.length} {t('incomeExpense.months')}
                                </span>
                            </div>
                            <div className="divide-y divide-zinc-100 dark:divide-zinc-800/40">
                                {[...summary.byMonth].reverse().slice(0, 6).map((m) => (
                                    <div key={m.month} className="flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-800/30 flex items-center justify-center flex-shrink-0">
                                                <IconCalendarStats size={15} className="text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                            <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{fmtMonth(m.month, isBn)}</span>
                                        </div>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="flex items-center gap-4 text-xs font-semibold cursor-default">
                                                    <span className="text-emerald-600 dark:text-emerald-400">+৳{fmtAmount(m.income, isBn)}</span>
                                                    <span className="text-red-500 dark:text-red-400">-৳{fmtAmount(m.expense, isBn)}</span>
                                                    {(m.due || 0) > 0 && (
                                                        <span className="text-amber-600 dark:text-amber-400">৳{fmtAmount(m.due, isBn)} {t('incomeExpense.due')}</span>
                                                    )}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>{t('incomeExpense.balance')}: {m.balance < 0 ? '-' : ''}৳{fmtAmount(Math.abs(m.balance), isBn)}</TooltipContent>
                                        </Tooltip>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Filters ──────────────────────────────────────────────────── */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
                {/* Period row */}
                <div className="px-5 py-3.5 border-b border-zinc-100 dark:border-zinc-800/60">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 shrink-0 min-w-[52px]">
                            <IconCalendarStats size={12} />
                            {t('incomeExpense.filterPeriod')}
                        </div>

                        {/* Desktop: chips */}
                        <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                            {PRESETS.map(p => (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => applyPreset(p)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                                        activePreset === p.key
                                            ? 'bg-primary border-primary text-white'
                                            : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-primary/50 hover:text-primary dark:hover:text-primary'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}

                            {/* Custom date range */}
                            <button
                                type="button"
                                onClick={() => { setActivePreset('custom'); const t0 = fmt(new Date()); setDateFrom((v) => v || t0); setDateTo((v) => v || t0); }}
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                                    activePreset === 'custom'
                                        ? 'bg-primary border-primary text-white'
                                        : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-primary/50 hover:text-primary dark:hover:text-primary'
                                }`}
                            >
                                <IconCalendarStats size={12} />
                                {t('incomeExpense.presetCustom')}
                            </button>
                        </div>

                        {/* Mobile: compact dropdown of the same presets (+ custom) */}
                        <div className="sm:hidden flex-1 min-w-0">
                            <SelectField
                                value={activePreset}
                                onChange={(key) => {
                                    if (key === 'custom') {
                                        setActivePreset('custom');
                                        const t0 = fmt(new Date());
                                        setDateFrom((v) => v || t0);
                                        setDateTo((v) => v || t0);
                                        return;
                                    }
                                    const p = PRESETS.find(x => x.key === key);
                                    if (p) applyPreset(p);
                                }}
                                options={[
                                    ...PRESETS.map(p => ({ value: p.key, label: p.label })),
                                    { value: 'custom', label: t('incomeExpense.presetCustom') },
                                ]}
                                triggerClassName="w-full flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-700 dark:text-zinc-200"
                            />
                        </div>
                    </div>

                    {/* Custom date range pickers — shown on both layouts when custom is active. */}
                    {activePreset === 'custom' && (
                        <div className="mt-2.5 flex items-center gap-1.5 sm:pl-[64px]">
                            <div className="flex-1 sm:flex-none sm:w-[150px]">
                                <DatePickerField
                                    value={parseISO(dateFrom)}
                                    onChange={(d) => { setDateFrom(d ? toISO(d) : ''); setActivePreset('custom'); }}
                                    placeholder={t('incomeExpense.fromDate')}
                                    triggerClassName="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-2.5 py-1.5 text-xs text-zinc-700 dark:text-zinc-200"
                                    disabled={{ after: dateTo ? parseISO(dateTo) : new Date() }}
                                />
                            </div>
                            <span className="text-xs text-zinc-400">—</span>
                            <div className="flex-1 sm:flex-none sm:w-[150px]">
                                <DatePickerField
                                    value={parseISO(dateTo)}
                                    onChange={(d) => { setDateTo(d ? toISO(d) : ''); setActivePreset('custom'); }}
                                    placeholder={t('incomeExpense.toDate')}
                                    triggerClassName="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-2.5 py-1.5 text-xs text-zinc-700 dark:text-zinc-200"
                                    disabled={[{ after: new Date() }, ...(dateFrom ? [{ before: parseISO(dateFrom) }] : [])]}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Type (left) + Cash/Due (right) on one row */}
                <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-3.5">
                    {/* Type */}
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-zinc-500 shrink-0">
                            {t('incomeExpense.filterType')}
                        </span>
                        <div className="flex items-center gap-1.5">
                            {[
                                { val: '',        label: t('incomeExpense.typeAll'),  cls: filterType === ''        ? 'bg-zinc-700 dark:bg-zinc-600 border-zinc-700 dark:border-zinc-600 text-white'          : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-500' },
                                { val: 'income',  label: t(isDena ? 'incomeExpense.pawna' : 'incomeExpense.income'),   cls: filterType === 'income'  ? 'bg-emerald-500 border-emerald-500 text-white'     : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-400' },
                                { val: 'expense', label: t(isDena ? 'incomeExpense.dena' : 'incomeExpense.expense'),  cls: filterType === 'expense' ? 'bg-red-500 border-red-500 text-white'             : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-red-500/50 hover:text-red-600 dark:hover:text-red-400' },
                                ...(isDena ? [{ val: 'settlement', label: t('incomeExpense.settlement'), cls: filterType === 'settlement' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-indigo-500/50 hover:text-indigo-600 dark:hover:text-indigo-400' }] : []),
                            ].map(({ val, label, cls }) => (
                                <button
                                    key={val}
                                    type="button"
                                    onClick={() => setFilterType(val)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${cls}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Cash / Due — hidden in দেনা পাওনা (always baki there) */}
                    {!isDena && (
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-zinc-500 shrink-0">
                            {t('incomeExpense.cash')} / {t('incomeExpense.due')}
                        </span>
                        <div className="flex items-center gap-1.5">
                            {[
                                { val: '',      label: t('incomeExpense.typeAll'), cls: dueFilter === ''      ? 'bg-zinc-700 dark:bg-zinc-600 border-zinc-700 dark:border-zinc-600 text-white' : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-500' },
                                { val: 'false', label: t('incomeExpense.cash'),    cls: dueFilter === 'false' ? 'bg-zinc-700 dark:bg-zinc-600 border-zinc-700 dark:border-zinc-600 text-white' : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-500' },
                                { val: 'true',  label: t('incomeExpense.due'),     cls: dueFilter === 'true'  ? 'bg-primary border-primary text-white'                                          : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:border-primary/50 hover:text-primary dark:hover:text-primary' },
                            ].map(({ val, label, cls }) => (
                                <button
                                    key={val || 'all'}
                                    type="button"
                                    onClick={() => setDueFilter(val)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${cls}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    )}
                </div>

                {/* Shop + operator (owner only) */}
                {hasOperatorFilters && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3.5 border-t border-zinc-100 dark:border-zinc-800/60">
                        {ownedSpaces.length > 1 && (
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-medium text-zinc-500 shrink-0">{t('operatorPay.shopFilter')}</span>
                                <div className="w-[190px]">
                                    <SelectField
                                        value={shopFilter}
                                        onChange={(v) => { setShopFilter(v); setOperatorKey(''); }}
                                        options={shopOptions}
                                        clearable
                                        triggerClassName="w-full flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <span className="text-xs font-medium text-zinc-500 shrink-0">{t('operatorPay.operatorFilter')}</span>
                            <div className="w-[220px]">
                                <SelectField
                                    value={operatorKey}
                                    onChange={setOperatorKey}
                                    options={operatorOptions}
                                    clearable
                                    triggerClassName="w-full flex items-center justify-between rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* What the selected operator entered (income / expense) and what they earn from it */}
            {operatorKey && opSpaceId && opId && !isDena && (
                <OperatorPayCard
                    officeSpaceId={opSpaceId}
                    operatorId={opId}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                />
            )}

            {/* ── Recent Transactions (card-style matching screenshot) ─────── */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
                {/* Header — mobile stacks the actions BELOW the title so both get room */}
                <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-100 dark:border-zinc-800/60">
                    <div className="flex items-center gap-2">
                        <IconReceipt2 size={18} className="text-zinc-500 dark:text-zinc-400" />
                        <h3 className="text-[15px] sm:text-base font-semibold text-zinc-800 dark:text-zinc-200">{t('incomeExpense.recentTransactions')}</h3>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={entries.length === 0 || !!exportBusy}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 text-[13px] sm:text-sm font-medium border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-600 disabled:opacity-30 transition-colors"
                        >
                            {exportBusy === 'print'
                                ? <span className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                                : <IconPrinter size={15} />}
                            {t('incomeExpense.print')}
                        </button>
                        <button
                            type="button"
                            onClick={handleExportPdf}
                            disabled={entries.length === 0 || !!exportBusy}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 text-[13px] sm:text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-30 transition-colors"
                        >
                            {exportBusy === 'pdf'
                                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconFileTypePdf size={15} />}
                            {t('incomeExpense.exportPdf')}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs text-zinc-500">{t('common.loading')}</p>
                    </div>
                ) : entries.length === 0 ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <IconReceipt2 size={28} className="text-zinc-400 dark:text-zinc-600" />
                        </div>
                        <p className="text-sm text-zinc-500">{t('incomeExpense.noEntries')}</p>
                    </div>
                ) : (
                    <>
                    {/* Desktop: table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full min-w-[680px] border-collapse text-sm">
                            <thead>
                                <tr className="text-left">
                                    {[
                                        { label: t('incomeExpense.name') },
                                        { label: t('incomeExpense.colType') },
                                        { label: t('incomeExpense.colCategory') },
                                        { label: t('incomeExpense.colDate') },
                                        { label: t('incomeExpense.amount'), align: 'text-right' },
                                        { label: '' },
                                    ].map((h, i) => (
                                        <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${h.align || ''}`}>{h.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((e) => (
                                    <tr
                                        key={e.id}
                                        className="border-b border-zinc-100 dark:border-zinc-800/40 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group"
                                    >
                                        {/* Name */}
                                        <td className="py-[13px] px-4 first:pl-5">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{e.name}</span>
                                                {e.enteredByName && e.enteredById !== user?.id && (
                                                    <span className="flex-shrink-0 text-[10px] font-medium text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/20 px-1.5 py-0.5 rounded">
                                                        {t('operatorPay.enteredBy')}: {e.enteredByName}
                                                    </span>
                                                )}
                                                {e.isDue && !isDena && (
                                                    <span className="flex-shrink-0 text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                                                        {t('incomeExpense.due')}
                                                    </span>
                                                )}
                                                {settleChip(e)}
                                            </div>
                                            {custOf(e) && (
                                                <p className="mt-1 flex items-center gap-1 text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                                    <IconUser size={12} className="flex-none text-zinc-400" />
                                                    {custOf(e)}
                                                </p>
                                            )}
                                            {e.note && (
                                                <p className="mt-1 max-w-[280px] whitespace-normal break-words text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                                    {e.note}
                                                </p>
                                            )}
                                        </td>

                                        {/* Type */}
                                        <td className="py-[13px] px-4">
                                            <span className={`inline-flex items-center gap-1.5 rounded-[7px] px-2 py-1 text-[12px] font-semibold whitespace-nowrap ${rowBadgeCls(e)}`}>
                                                {rowBadgeIcon(e, 13)}
                                                {rowBadgeLabel(e)}
                                            </span>
                                        </td>

                                        {/* Category */}
                                        <td className="py-[13px] px-4 text-[13.5px] text-zinc-600 dark:text-zinc-300">{CAT_LABELS[e.category] || e.category}</td>

                                        {/* Date + BD time */}
                                        <td className="py-[13px] px-4 text-[13px] text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                                            {fmtDate(e.createdAt, isBn)}
                                            <span className="ml-1.5 text-[11.5px] text-zinc-400 dark:text-zinc-500">{fmtTime(e.createdAt, isBn)}</span>
                                        </td>

                                        {/* Amount */}
                                        <td className={`py-[13px] px-4 text-right text-sm font-bold tabular-nums whitespace-nowrap ${rowAmountCls(e)}`}>
                                            {rowAmountSign(e)}৳{fmtAmount(e.amount, isBn)}
                                        </td>

                                        {/* Actions */}
                                        <td className="py-[13px] px-4 last:pr-5 text-right whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => startEdit(e)}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-500/10 transition-colors"
                                                        >
                                                            <IconPencil size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('common.edit') || 'এডিট'}</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfirmId(e.id)}
                                                            disabled={deleting === e.id}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-500/10 disabled:opacity-30 transition-colors"
                                                        >
                                                            <IconTrash size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('common.delete')}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile: card list — same data, no horizontal scroll */}
                    <div className="sm:hidden divide-y divide-zinc-100 dark:divide-zinc-800/50">
                        {entries.map((e) => (
                            <div key={e.id} className="px-4 py-3.5 flex flex-col gap-2.5">
                                {/* Top: name + amount */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{e.name}</span>
                                            {e.enteredByName && e.enteredById !== user?.id && (
                                                <span className="flex-shrink-0 text-[10px] font-medium text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/20 px-1.5 py-0.5 rounded">
                                                    {t('operatorPay.enteredBy')}: {e.enteredByName}
                                                </span>
                                            )}
                                            {e.isDue && !isDena && (
                                                <span className="flex-shrink-0 text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                                                    {t('incomeExpense.due')}
                                                </span>
                                            )}
                                            {settleChip(e)}
                                        </div>
                                        {custOf(e) && (
                                            <p className="mt-1 flex items-center gap-1 text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                                <IconUser size={12} className="flex-none text-zinc-400" />
                                                {custOf(e)}
                                            </p>
                                        )}
                                        {e.note && (
                                            <p className="mt-1 whitespace-normal break-words text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                                {e.note}
                                            </p>
                                        )}
                                    </div>
                                    <span className={`text-sm font-bold tabular-nums whitespace-nowrap ${rowAmountCls(e)}`}>
                                        {rowAmountSign(e)}৳{fmtAmount(e.amount, isBn)}
                                    </span>
                                </div>

                                {/* Meta: type · category · date */}
                                <div className="flex items-center gap-2 flex-wrap text-[12px] text-zinc-500 dark:text-zinc-400">
                                    <span className={`inline-flex items-center gap-1 rounded-[7px] px-2 py-0.5 text-[11.5px] font-semibold ${rowBadgeCls(e)}`}>
                                        {rowBadgeIcon(e, 12)}
                                        {rowBadgeLabel(e)}
                                    </span>
                                    <span className="text-zinc-300 dark:text-zinc-600">·</span>
                                    <span>{CAT_LABELS[e.category] || e.category}</span>
                                    <span className="text-zinc-300 dark:text-zinc-600">·</span>
                                    <span className="whitespace-nowrap">
                                        {fmtDate(e.createdAt, isBn)}
                                        <span className="ml-1 text-[11px] text-zinc-400 dark:text-zinc-500">{fmtTime(e.createdAt, isBn)}</span>
                                    </span>
                                    {/* Actions — always visible on mobile */}
                                    <span className="ml-auto inline-flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => startEdit(e)}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-500/10 transition-colors"
                                        >
                                            <IconPencil size={15} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setConfirmId(e.id)}
                                            disabled={deleting === e.id}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-500/10 disabled:opacity-30 transition-colors"
                                        >
                                            <IconTrash size={15} />
                                        </button>
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    </>
                )}
            </div>

            {/* ── Pagination (shared bar: item range + numbered pages) ─────────── */}
            <PaginationBar
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={15}
                currentCount={entries.length}
                onPage={load}
                className="justify-center sm:justify-between"
            />

            {/* ── পরিশোধ (দেনা পাওনা settlement) add / edit ─────────────────── */}
            <SettlementModal
                open={settleModal.open}
                entry={settleModal.entry}
                onClose={(saved) => { setSettleModal({ open: false, entry: null }); if (saved) load(settleModal.entry ? page : 1); }}
            />

            {/* ── Delete confirm dialog ────────────────────────────────────── */}
            <Dialog open={confirmId !== null} onOpenChange={(open) => { if (!open) setConfirmId(null); }}>
                <DialogContent className="max-w-sm p-0 overflow-hidden">
                    <div className="p-6 flex flex-col items-center text-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-red-500/10 dark:bg-red-900/20 flex items-center justify-center">
                            <IconAlertTriangle size={26} className="text-red-500" />
                        </div>
                        <div>
                            <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{t('incomeExpense.deleteConfirmTitle')}</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('incomeExpense.deleteConfirmDesc')}</p>
                        </div>
                    </div>
                    <div className="flex border-t border-zinc-100 dark:border-zinc-800">
                        <button
                            type="button"
                            onClick={() => setConfirmId(null)}
                            className="flex-1 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-r border-zinc-100 dark:border-zinc-800"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                            {t('common.delete')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
            </>
            )}
        </div>
    );
}
